/**
 * Utility functions for handling subcircle data
 */

/**
 * Extract a readable subcircle name from a subcircle ID
 * Examples:
 * "bridge-bound-high-grade-3-1754775734714" -> "Grade 3"
 * "bridge-bound-high-year-12-1754775734714" -> "Year 12"
 * "bridge-bound-high-form-5a-1754775734714" -> "Form 5A"
 */
export const parseSubcircleName = (subcircleId: string): string => {
  if (!subcircleId) return "Specific Subcircle";

  // Split by dashes and look for grade/year/form patterns
  const parts = subcircleId.split("-");

  // Look for patterns like "grade-3", "year-12", "form-5a", etc.
  for (let i = 0; i < parts.length - 1; i++) {
    const current = parts[i].toLowerCase();
    const next = parts[i + 1];

    if (current === "grade" || current === "year" || current === "form") {
      // Capitalize the first letter and combine with the next part
      const prefix = current.charAt(0).toUpperCase() + current.slice(1);
      const suffix = next.toUpperCase(); // Uppercase for things like "5A"
      return `${prefix} ${suffix}`;
    }
  }

  // If no pattern found, try to find any numeric part and assume it's a grade
  const numericPart = parts.find((part) => /^\d+[a-zA-Z]*$/.test(part));
  if (numericPart) {
    return `Grade ${numericPart.toUpperCase()}`;
  }

  // Fallback: return a cleaned version of the ID
  const cleanName = parts
    .filter((part) => !/^\d{13}$/.test(part)) // Remove timestamp
    .join(" ")
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special chars
    .replace(/\s+/g, " ") // Normalize spaces
    .trim();

  return cleanName || "Specific Subcircle";
};
